import React, { memo, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { StyleSheet, TouchableWithoutFeedback, View, } from 'react-native';
import Animated from 'react-native-reanimated';
import { FlatList } from 'react-native-gesture-handler';
import { Spacer, CardView, Indicator, TextButtonLinkSeeAll, SCREEN_BOTTOM_PADDING, rem, } from 'design-system';
import Section from 'design-system/Section';
import { RewardsCarousel } from 'features/rewards/components/RewardsCarousel';
import { RewardsGroup } from 'features/rewards/components/RewardsGroup';
import { useRewards } from 'features/rewards/hooks/useRewards';
import { useFocusEffect, useHeaderHeight } from 'hooks';
import { RewardTypes } from 'reducers/types';
import Amplitude from 'utils/amplitude';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { getRedemptions } from 'actions';
import { useAppDispatch } from 'store/hooks';
import FakeSearchBar from 'design-system/FakeSearchBar';
import { TAB_SCREEN_HEADER_HEIGHT } from 'navigators/styles';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { REWARD_TYPES } from '../utils';
import Disclaimer from './Disclaimer';
import { RewardListItem } from './RewardListItem';
import { isWeb } from '../../../constants';
const rewardTypesToShow = [
    RewardTypes.TopOffers,
    RewardTypes.NewOffers,
    RewardTypes.Home,
    RewardTypes.Food,
    RewardTypes.Health,
    RewardTypes.Fashion,
    RewardTypes.Entertainment,
    RewardTypes.Tech,
    RewardTypes.Beauty,
    RewardTypes.Travel,
];
const keyExtractor = (item) => typeof item === 'string' ? item : item?.id.toString();
const AnimatedFlatList = Animated.createAnimatedComponent(FlatList);
const RewardsScreenContent = ({ scrollViewRef, onScroll, isTabActive, isInTab, scrollY, ListHeader, listContainerStyle, extraScrollYOffset = 0, }) => {
    const { top } = useSafeAreaInsets();
    const navigation = useMainStackNavigation();
    const [renderData, setRenderData] = useState(false);
    const { fetchRewards, isRewardsFetching, hasRewards, featuredRewards, normalizedByTypeRewardsSliced, filteredBySearchRewards, openReward, } = useRewards();
    const { navigateDeprecated } = useMainStackNavigation();
    const dispatch = useAppDispatch();
    const headerHeight = useHeaderHeight();
    const { paddingHorizontalStyle, paddingHorizontal } = useAppFrameDimensions(isWeb || isInTab);
    useEffect(() => {
        dispatch(getRedemptions());
        const timeout = setTimeout(() => {
            setRenderData(true);
        }, 200);
        return () => {
            clearTimeout(timeout);
        };
    }, []);
    const initialCallRan = useRef(false);
    useEffect(() => {
        if (!isRewardsFetching && !hasRewards && initialCallRan.current) {
            fetchRewards();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTabActive]);
    useFocusEffect(useCallback(() => {
        if (!isRewardsFetching && !hasRewards) {
            fetchRewards().then(() => {
                initialCallRan.current = true;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchRewards, hasRewards]));
    const onOpenReward = useCallback((reward) => {
        Amplitude.logEvent('Open Reward', { source: 'rewards', reward });
        openReward(reward);
    }, [openReward]);
    const handleSeeAll = useCallback((item) => () => {
        navigateDeprecated('RewardCategory', {
            rewardType: item,
        });
    }, []);
    const renderItem = useCallback(({ item, index }) => {
        if (typeof item === 'string') {
            const rewards = normalizedByTypeRewardsSliced[item];
            if (!rewards?.length)
                return null;
            return (<Section key={item} title={REWARD_TYPES[item].text} button={<TextButtonLinkSeeAll onPress={handleSeeAll(item)}/>} titleContainerStyle={paddingHorizontalStyle}>
            <View style={paddingHorizontalStyle}>
              <CardView noMarginTop noMarginBottom>
                <RewardsGroup largerText rewards={rewards} onRewardSelect={onOpenReward}/>
              </CardView>
            </View>
          </Section>);
        }
        return (<View style={paddingHorizontalStyle}>
          <RewardListItem key={item.id} index={index} itemsLength={filteredBySearchRewards.length} reward={item} onPress={onOpenReward}/>
        </View>);
    }, [
        filteredBySearchRewards.length,
        handleSeeAll,
        normalizedByTypeRewardsSliced,
        onOpenReward,
        paddingHorizontalStyle,
    ]);
    const openSearch = useCallback(() => {
        navigation.navigateDeprecated('SearchRewards', {
            searchBarY: isInTab
                ? TAB_SCREEN_HEADER_HEIGHT + rem(53) + top - scrollY.value
                : headerHeight + extraScrollYOffset,
        });
    }, [extraScrollYOffset, headerHeight, isInTab, scrollY.value, top]);
    const ListHeaderComponent = useMemo(() => (<>
        {ListHeader}
        <View style={paddingHorizontalStyle}>
          <TouchableWithoutFeedback onPress={openSearch}>
            <View pointerEvents="box-only">
              <FakeSearchBar placeholder="Search cashback"/>
            </View>
          </TouchableWithoutFeedback>
        </View>
        <Spacer h={20}/>
        {renderData ? (<Section title={REWARD_TYPES.POPULAR.text} button={<TextButtonLinkSeeAll onPress={() => {
                    navigateDeprecated('RewardCategory', {
                        rewardType: RewardTypes.Popular,
                    });
                }}/>} titleContainerStyle={paddingHorizontalStyle}>
            <RewardsCarousel data={featuredRewards} onPress={onOpenReward} contentContainerStyle={paddingHorizontalStyle} paddingHorizontal={paddingHorizontal}/>
          </Section>) : null}
      </>), [
        ListHeader,
        paddingHorizontalStyle,
        openSearch,
        renderData,
        featuredRewards,
        onOpenReward,
        paddingHorizontal,
    ]);
    const flatListData = useMemo(() => {
        if (renderData) {
            return rewardTypesToShow;
        }
        return [];
    }, [renderData]);
    const ListFooterComponent = useMemo(() => {
        if (!renderData)
            return null;
        return (<View style={paddingHorizontalStyle}>
        <Disclaimer />
      </View>);
    }, [paddingHorizontalStyle, renderData]);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING);
    if (isRewardsFetching || !hasRewards) {
        return (<View style={styles.indicatorContainer}>
        <Indicator dark/>
      </View>);
    }
    return (<View style={styles.container}>
      <AnimatedFlatList ref={scrollViewRef} onScroll={onScroll} data={flatListData} initialNumToRender={4} renderItem={renderItem} scrollEventThrottle={16} keyExtractor={keyExtractor} keyboardShouldPersistTaps="handled" ListFooterComponent={ListFooterComponent} ListHeaderComponent={ListHeaderComponent} contentContainerStyle={listContainerStyle || contentContainerStyle}/>
    </View>);
};
const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    indicatorContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
});
const component = memo(RewardsScreenContent);
component.whyDidYouRender = true;
export default component;
